/**
 * U-04 «Мои фотографии», U-05 «Добавление фото», U-09 «Статус модерации»,
 * U-14 «Ошибка загрузки».
 *
 * ПС §07: пользователь выбирает главное фото и меняет порядок остальных.
 * Изменение v1.11: фото необязательно и публикуется сразу — ждать проверки не нужно,
 * удалить можно любое, включая последнее. Статус остаётся только у снятого модерацией
 * фото и виден ТОЛЬКО владельцу, с понятной причиной.
 */
import { useRef, useState } from 'react'

import { PHOTO_REJECTED_LABEL, photoRejectionText } from '@entities/user/photo-state'
import { useMediaUpload } from '@features/media/use-media-upload'
import { api, idempotency, unwrap, uuid } from '@shared/api/client'
import { ApiError } from '@shared/api/errors'
import type { ProfilePhoto } from '@shared/api/types'
import { acceptAttribute } from '@shared/media/limits'
import { useHasPointerFine } from '@shared/lib/use-media-query'
import { Banner, Button, Dialog, Icon, Spinner, cx, useToast } from '@shared/ui'

import { Screen } from '../../layouts/Screen'
import { refreshMe } from '../../boot/refresh-me'
import { useAppStore } from '../../model/app-store'
import styles from './photos.module.css'

export function PhotosScreen(): React.ReactElement {
  return (
    <Screen title="Фотографии" back narrow>
      <PhotosManager />
    </Screen>
  )
}

/**
 * Управление фотографиями без обёртки экрана: используется и в U-04, и на шаге
 * onboarding O-03 (там — со своим вступлением и кнопкой «Продолжить»).
 */
export function PhotosManager({ intro }: { intro?: React.ReactNode } = {}): React.ReactElement {
  const toast = useToast()
  const me = useAppStore((s) => s.me)
  const config = useAppStore((s) => s.config)
  const hasPointerFine = useHasPointerFine()

  const fileInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const [pendingDelete, setPendingDelete] = useState<ProfilePhoto | null>(null)
  const [busy, setBusy] = useState(false)

  const upload = useMediaUpload({
    config,
    purpose: 'profile_photo',
    accountStatus: me?.user.status,
  })

  const photos = me?.profile.photos ?? []
  const maxPhotos = config?.limits.photos_max ?? 9
  const canAddMore = photos.length < maxPhotos

  /* --- Добавление ------------------------------------------------------ */

  const handleFile = async (file: File | undefined): Promise<void> => {
    // §10.1 п. 2: отмена диалога выбора — не ошибка, молчим.
    if (!file) return

    const mediaId = await upload.uploadFile(file)
    if (!mediaId) return

    try {
      unwrap(
        await api.POST('/me/profile/photos', {
          body: { media_id: mediaId } as never,
          ...idempotency(uuid()),
        }),
      )
      await refreshMe()
      upload.reset()
      toast.show('Фото добавлено')
    } catch (error) {
      toast.show(describePhotoError(error), { tone: 'danger' })
    }
  }

  /* --- Порядок --------------------------------------------------------- */

  const movePhoto = async (index: number, direction: -1 | 1): Promise<void> => {
    const next = [...photos]
    const target = index + direction
    if (target < 0 || target >= next.length) return

    const a = next[index]!
    const b = next[target]!
    next[index] = b
    next[target] = a

    setBusy(true)
    try {
      await api.PUT('/me/profile/photos/order', {
        body: { photo_ids: next.map((p) => p.id) } as never,
      })
      await refreshMe()
    } catch {
      toast.show('Не удалось изменить порядок', { tone: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  /* --- Удаление --------------------------------------------------------- */

  const deletePhoto = async (photo: ProfilePhoto): Promise<void> => {
    setBusy(true)
    try {
      await api.DELETE('/me/profile/photos/{photoId}', { params: { path: { photoId: photo.id } } })
      await refreshMe()
      toast.show('Фото удалено')
    } catch {
      toast.show('Не удалось удалить фото', { tone: 'danger' })
    } finally {
      setBusy(false)
      setPendingDelete(null)
    }
  }

  const accept = acceptAttribute(upload.limit)

  return (
    <>
      {intro}

      {/* U-14: ошибка загрузки с понятной причиной и повтором */}
      {upload.state.error ? (
        <Banner
          tone="danger"
          title="Не удалось добавить фото"
          className={styles['error']}
          onDismiss={upload.reset}
          actions={
            <Button size="sm" variant="secondary" onClick={() => fileInputRef.current?.click()}>
              Выбрать другое
            </Button>
          }
        >
          {upload.state.error}
        </Banner>
      ) : null}

      <div className={styles['grid']}>
        {photos.map((photo, index) => (
          <PhotoTile
            key={photo.id}
            photo={photo}
            index={index}
            total={photos.length}
            busy={busy}
            showArrows={hasPointerFine || true}
            onMove={(direction) => void movePhoto(index, direction)}
            onDelete={() => setPendingDelete(photo)}
          />
        ))}

        {/* Плитка загрузки: прогресс передачи, затем «обработка» (§8.7 п. 1) */}
        {upload.state.stage !== 'idle' && upload.state.stage !== 'failed' ? (
          <UploadingTile
            previewUrl={upload.state.previewUrl}
            stage={upload.state.stage}
            progress={upload.state.progress}
            onCancel={upload.cancel}
          />
        ) : null}

        {canAddMore && upload.state.stage === 'idle' ? (
          <button type="button" className={styles['addTile']} onClick={() => fileInputRef.current?.click()}>
            <Icon name="plus" size={26} />
            <span>Добавить</span>
          </button>
        ) : null}
      </div>

      {/* Сноска под сеткой вместо баннера сверху — как серый текст под группой в iOS */}
      <p className={styles['counter']}>
        Первое фото — главное: его видят в подборке. Без фото в «Движе» вас не показывают.
      </p>
      <p className={styles['counter']}>
        {photos.length} из {maxPhotos}
      </p>

      {/* §10.1: на mobile-раскладке доступна съёмка; на desktop `capture` игнорируется */}
      {!hasPointerFine && canAddMore ? (
        <div className={styles['actions']}>
          <Button
            variant="secondary"
            fullWidth
            iconStart="camera"
            onClick={() => cameraInputRef.current?.click()}
          >
            Снять на камеру
          </Button>
        </div>
      ) : null}

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        // §11.3 п. 4: скрытое поле остаётся в дереве доступности.
        aria-label="Выбрать фотографию"
        className={styles['hiddenInput']}
        onChange={(event) => {
          void handleFile(event.target.files?.[0])
          event.target.value = ''
        }}
      />

      <input
        ref={cameraInputRef}
        type="file"
        accept={accept}
        capture="user"
        aria-label="Снять фото на камеру"
        className={styles['hiddenInput']}
        onChange={(event) => {
          void handleFile(event.target.files?.[0])
          event.target.value = ''
        }}
      />

      <Dialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title="Удалить фотографию?"
        description="Её больше не будет в вашей анкете."
        footer={
          <>
            <Button variant="secondary" onClick={() => setPendingDelete(null)}>
              Отмена
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                if (pendingDelete) void deletePhoto(pendingDelete)
              }}
            >
              Удалить
            </Button>
          </>
        }
      />
    </>
  )
}

/* ===================================================================== */
/* Плитка фотографии                                                      */
/* ===================================================================== */

function PhotoTile({
  photo,
  index,
  total,
  busy,
  onMove,
  onDelete,
}: {
  photo: ProfilePhoto
  index: number
  total: number
  busy: boolean
  showArrows: boolean
  onMove: (direction: -1 | 1) => void
  onDelete: () => void
}) {
  const url = photo.variants.find((v) => v.name === 'medium')?.url ?? photo.variants[0]?.url
  const reason = photoRejectionText(photo)

  return (
    <figure className={styles['tile']}>
      {url ? (
        <img src={url} alt="" className={styles['image']} />
      ) : (
        <div className={styles['imagePlaceholder']} />
      )}

      {index === 0 ? <span className={styles['mainBadge']}>Главное</span> : null}

      {/* U-09: снятое модерацией фото объясняется только владельцу, с категорией причины */}
      {reason ? (
        <span className={cx(styles['state'], styles['stateRejected'])}>
          {PHOTO_REJECTED_LABEL}
          <span className={styles['stateReason']}>{reason}</span>
        </span>
      ) : null}

      {/* Кнопки только возможных перемещений: иначе на узкой плитке ряд наезжает на «Главное» */}
      <div className={styles['tileActions']}>
        {index > 0 ? (
          <button
            type="button"
            className={styles['tileButton']}
            aria-label="Переместить назад"
            disabled={busy}
            onClick={() => onMove(-1)}
          >
            <Icon name="back" size={16} />
          </button>
        ) : null}

        {index < total - 1 ? (
          <button
            type="button"
            className={styles['tileButton']}
            aria-label="Переместить вперёд"
            disabled={busy}
            onClick={() => onMove(1)}
          >
            <Icon name="forward" size={16} />
          </button>
        ) : null}

        <button
          type="button"
          className={cx(styles['tileButton'], styles['tileDanger'])}
          aria-label="Удалить фото"
          disabled={busy}
          onClick={onDelete}
        >
          <Icon name="trash" size={16} />
        </button>
      </div>
    </figure>
  )
}

/* ===================================================================== */
/* Плитка загрузки (§8.7 п. 1: два визуально разных этапа)                 */
/* ===================================================================== */

function UploadingTile({
  previewUrl,
  stage,
  progress,
  onCancel,
}: {
  previewUrl: string | null
  stage: string
  progress: number
  onCancel: () => void
}) {
  const uploading = stage === 'uploading' || stage === 'preparing'

  return (
    <figure className={styles['tile']}>
      {previewUrl ? (
        <img src={previewUrl} alt="" className={cx(styles['image'], styles['imageDimmed'])} />
      ) : null}

      <div className={styles['uploadOverlay']}>
        {uploading ? (
          <>
            <div className={styles['progressRing']} aria-label={`Загружено ${Math.round(progress * 100)}%`}>
              {Math.round(progress * 100)}%
            </div>
            <div className={styles['progressBar']}>
              <div className={styles['progressFill']} style={{ width: `${progress * 100}%` }} />
            </div>
          </>
        ) : (
          <>
            <Spinner size={24} />
            <span className={styles['uploadLabel']}>Обработка…</span>
          </>
        )}

        <button type="button" className={styles['cancelButton']} onClick={onCancel}>
          Отменить
        </button>
      </div>
    </figure>
  )
}

function describePhotoError(error: unknown): string {
  if (!(error instanceof ApiError)) return 'Не удалось добавить фото'

  switch (error.code) {
    case 'photo_limit_reached':
      return 'Достигнут предел количества фотографий'
    case 'media_not_ready':
      return 'Фото ещё обрабатывается, попробуйте через секунду'
    case 'media_invalid_purpose':
      return 'Этот файл нельзя использовать как фото профиля'
    default:
      return 'Не удалось добавить фото'
  }
}
